import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { UserManagementService, UserListResponse, ChangeRoleRequest, ResetPasswordResponse } from '../../services/user-management.service';
import { User } from '../../services/auth.service';
import { RoleManagementService, Role } from '../../services/role-management.service';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-user-management',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './user-management.component.html',
  styleUrls: ['./user-management.component.scss']
})
export class UserManagementComponent implements OnInit {
  users: User[] = [];
  roles: Role[] = [];
  totalUsers = 0;
  currentPage = 1;
  pageSize = 10;
  totalPages = 0;

  // Modal states
  showEditModal = false;
  showRoleModal = false;
  showStatusModal = false;
  showResetPasswordModal = false;

  // Selected user for operations
  selectedUser: User | null = null;
  
  // Reset password response
  resetPasswordResponse: ResetPasswordResponse | null = null;

  // Form data
  editForm = {
    first_name: '',
    last_name: '',
    phone: '',
    business_title: ''
  };

  roleForm = {
    role: ''
  };

  statusForm = {
    is_active: true
  };

  // Loading states
  loading = false;
  saving = false;

  // Error handling
  errorMessage = '';
  successMessage = '';

  constructor(
    private userService: UserManagementService,
    private roleService: RoleManagementService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.loadUsers();
    this.loadRoles();
  }

  loadUsers(): void {
    this.loading = true;
    const skip = (this.currentPage - 1) * this.pageSize;

    this.userService.getAllUsers(skip, this.pageSize).subscribe({
      next: (response: UserListResponse) => {
        this.users = response.users;
        this.totalUsers = response.total;
        this.totalPages = Math.ceil(this.totalUsers / this.pageSize);
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.errorMessage = 'Failed to load users';
        this.loading = false;
      }
    });
  }

  loadRoles(): void {
    this.roleService.getActiveRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
      },
      error: (error) => {
        console.error('Error loading roles:', error);
      }
    });
  }

  openEditModal(user: User): void {
    this.selectedUser = user;
    this.editForm = {
      first_name: user.first_name,
      last_name: user.last_name,
      phone: user.phone || '',
      business_title: user.business_title || ''
    };
    this.showEditModal = true;
    this.clearMessages();
  }

  openRoleModal(user: User): void {
    this.selectedUser = user;
    this.roleForm = {
      role: user.role
    };
    this.showRoleModal = true;
    this.clearMessages();
  }

  openStatusModal(user: User): void {
    this.selectedUser = user;
    this.statusForm = {
      is_active: user.is_active
    };
    this.showStatusModal = true;
    this.clearMessages();
  }

  openResetPasswordModal(user: User): void {
    this.selectedUser = user;
    this.resetPasswordResponse = null;
    this.showResetPasswordModal = true;
    this.clearMessages();
  }

  closeModals(): void {
    this.showEditModal = false;
    this.showRoleModal = false;
    this.showStatusModal = false;
    this.showResetPasswordModal = false;
    this.selectedUser = null;
    this.resetPasswordResponse = null;
    this.clearMessages();
  }

  updateUser(): void {
    if (!this.selectedUser) return;

    this.saving = true;
    this.userService.updateCurrentUser(this.editForm).subscribe({
      next: (updatedUser) => {
        // Update the user in the list
        const index = this.users.findIndex(u => u.id === this.selectedUser!.id);
        if (index !== -1) {
          this.users[index] = updatedUser;
        }
        this.successMessage = 'User updated successfully';
        this.saving = false;
        this.closeModals();
      },
      error: (error) => {
        console.error('Error updating user:', error);
        this.errorMessage = 'Failed to update user';
        this.saving = false;
      }
    });
  }

  changeUserRole(): void {
    if (!this.selectedUser) return;

    this.saving = true;
    const roleRequest: ChangeRoleRequest = {
      role: this.roleForm.role
    };

    this.userService.changeUserRole(this.selectedUser.id, roleRequest).subscribe({
      next: (updatedUser) => {
        // Update the user in the list
        const index = this.users.findIndex(u => u.id === this.selectedUser!.id);
        if (index !== -1) {
          this.users[index] = updatedUser;
        }
        this.successMessage = 'User role updated successfully';
        this.saving = false;
        this.closeModals();
      },
      error: (error) => {
        console.error('Error changing user role:', error);
        this.errorMessage = 'Failed to change user role';
        this.saving = false;
      }
    });
  }

  toggleUserStatus(): void {
    if (!this.selectedUser) return;

    this.saving = true;
    this.userService.toggleUserStatus(this.selectedUser.id, this.statusForm.is_active).subscribe({
      next: () => {
        // Update the user in the list
        const index = this.users.findIndex(u => u.id === this.selectedUser!.id);
        if (index !== -1) {
          this.users[index].is_active = this.statusForm.is_active;
        }
        this.successMessage = `User ${this.statusForm.is_active ? 'activated' : 'deactivated'} successfully`;
        this.saving = false;
        this.closeModals();
      },
      error: (error) => {
        console.error('Error toggling user status:', error);
        this.errorMessage = 'Failed to update user status';
        this.saving = false;
      }
    });
  }

  resetPassword(): void {
    if (!this.selectedUser) return;

    this.saving = true;
    this.userService.resetUserPassword(this.selectedUser.id).subscribe({
      next: (response: ResetPasswordResponse) => {
        this.resetPasswordResponse = response;
        this.successMessage = 'Password reset successfully';
        this.saving = false;
      },
      error: (error) => {
        console.error('Error resetting password:', error);
        this.errorMessage = error.error?.detail || 'Failed to reset password';
        this.saving = false;
      }
    });
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.successMessage = 'Copied to clipboard!';
      setTimeout(() => this.clearMessages(), 2000);
    }).catch(err => {
      console.error('Failed to copy:', err);
      this.errorMessage = 'Failed to copy to clipboard';
    });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadUsers();
  }

  getRoleDisplayName(roleName: string): string {
    // Return the actual role name from user object (always shows current value from database)
    return roleName;
  }

  getUserTypeDisplay(userType: string): string {
    if (userType === 'Company/brand') {
      return 'Company/Brand';
    } else if (userType === 'Agency') {
      return 'Agency';
    }
    return userType;
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }

  clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  // Check if current user can perform admin actions
  canManageUsers(): boolean {
    return this.authService.canAccessUserManagement();
  }
}
